import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, Loader2, LogOut, KeyRound, CheckCircle2, ArrowDownToLine,
  ArrowUpFromLine, Phone, AlertTriangle, Clock, RotateCcw, ClipboardCheck,
  Car, MapPin, MessageSquare, ClipboardList,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import ConditionReportFlow from '../components/staff/ConditionReportFlow';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'staff_portal_token';
const NAME_KEY = 'staff_portal_name';

const todayISO = () => new Date().toISOString().slice(0, 10);
const prettyDate = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

export default function StaffPortalMobile() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [staffName, setStaffName] = useState(() => localStorage.getItem(NAME_KEY) || '');

  if (!token) return <Passcode onAuth={(t, n) => { localStorage.setItem(TOKEN_KEY, t); localStorage.setItem(NAME_KEY, n); setToken(t); setStaffName(n); }} />;
  return <Today token={token} staffName={staffName} onLogout={() => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(NAME_KEY); setToken(''); }} />;
}

// ───────────────── Passcode Screen ─────────────────
function Passcode({ onAuth }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (code.length < 4) { toast.error('Enter the passcode'); return; }
    if (!name.trim()) { toast.error('Enter your name first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/staff-portal/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: code, staff_name: name.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Invalid passcode');
      toast.success(`Welcome, ${name.trim()}`, { position: 'top-center' });
      onAuth(data.token, name.trim());
    } catch (e) { toast.error(e.message); setCode(''); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A] flex flex-col" data-testid="staff-passcode">
      <Toaster position="top-center" theme="light" />
      <div className="px-5 pt-8 pb-6 border-b border-[#E5E5E5] bg-white">
        <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A] font-bold mb-3">
          <ChevronLeft className="h-3 w-3 mr-1" /> Back
        </Link>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E52B20] font-bold">ACE · Staff Portal</p>
        <h1 className="text-3xl font-black tracking-tight mt-2">Sign In</h1>
        <p className="text-xs text-[#666] mt-1">Today&apos;s checkouts &amp; returns</p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10">
        <KeyRound className="h-12 w-12 text-[#E5E5E5] mb-6" strokeWidth={1.5} />
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-2 self-start">Your Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Marco"
          className="w-full h-14 px-4 text-lg font-semibold bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mb-4 transition-colors"
          data-testid="staff-name-input"
        />
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-2 self-start">Passcode</label>
        <input
          type="password"
          inputMode="numeric"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="••••"
          autoFocus
          className="w-full h-16 px-4 text-center text-2xl font-bold tracking-[0.5em] bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none transition-colors"
          data-testid="staff-passcode-input"
          onKeyDown={e => e.key === 'Enter' && submit()}
        />
        <button
          onClick={submit}
          disabled={busy}
          className="w-full mt-6 h-14 bg-[#E52B20] hover:bg-[#C92218] active:scale-95 transition-all text-white font-bold uppercase tracking-[0.15em] disabled:opacity-50"
          data-testid="staff-passcode-submit"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Unlock'}
        </button>
        <p className="text-[10px] text-[#666] mt-6 max-w-xs text-center">Ask your manager for the staff portal passcode.</p>
      </div>
    </div>
  );
}

// ───────────────── Today's Operations Screen ─────────────────
function Today({ token, staffName, onLogout }) {
  const [date, setDate] = useState(todayISO());
  const [tab, setTab] = useState('checkouts');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently-open action sheet
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/staff-portal/today?date=${date}`, { headers: { 'X-Staff-Token': token } });
        if (r.status === 401) { onLogout(); return; }
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) toast.error(e.message); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [date, token, onLogout, reloadKey]);

  const load = () => setReloadKey(k => k + 1);

  const list = data ? (tab === 'checkouts' ? data.checkouts : data.returns) : [];
  const counts = data?.counts || {};

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A] pb-32" data-testid="staff-today">
      <Toaster position="top-center" theme="light" />

      {/* Hero */}
      <div className="px-5 pt-6 pb-4 border-b border-[#E5E5E5] bg-white sticky top-0 z-30">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E52B20] font-bold">ACE · Staff</p>
          <button onClick={onLogout} className="text-[10px] uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A] flex items-center gap-1 font-bold" data-testid="staff-logout">
            <LogOut className="h-3 w-3" /> Sign Out
          </button>
        </div>
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight">{prettyDate(date)}</h1>
            <p className="text-xs text-[#666] mt-0.5">Welcome, {staffName}</p>
          </div>
          <button
            onClick={load}
            className="text-[10px] uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A] flex items-center gap-1 font-bold"
            data-testid="staff-refresh"
          >
            <RotateCcw className="h-3 w-3" /> Refresh
          </button>
        </div>

        {/* Date selector */}
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="mt-3 w-full h-10 px-3 bg-white border border-[#E5E5E5] text-sm font-semibold focus:border-[#0A0A0A] outline-none transition-colors"
          data-testid="staff-date-input"
        />

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <TabButton
            active={tab === 'checkouts'}
            onClick={() => setTab('checkouts')}
            icon={<ArrowUpFromLine className="h-4 w-4" />}
            label="Checkouts"
            count={counts.checkouts_total || 0}
            open={counts.checkouts_open || 0}
            testid="tab-checkouts"
          />
          <TabButton
            active={tab === 'returns'}
            onClick={() => setTab('returns')}
            icon={<ArrowDownToLine className="h-4 w-4" />}
            label="Returns"
            count={counts.returns_total || 0}
            open={counts.returns_open || 0}
            testid="tab-returns"
          />
        </div>
      </div>

      {/* List */}
      <div className="px-5 pt-5 space-y-3">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#E5E5E5]" /></div>
        ) : list.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          list.map(item => (
            <ItemCard
              key={`${item.source}-${item.id}`}
              item={item}
              tab={tab}
              onTap={() => setActive(item)}
            />
          ))
        )}
      </div>

      {/* Action Sheet */}
      {active && (
        <ActionSheet
          item={active}
          tab={tab}
          token={token}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

const TabButton = ({ active, onClick, icon, label, count, open, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`h-16 border transition-all ${active ? 'bg-[#0A0A0A] border-[#0A0A0A] text-white' : 'bg-white border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0A0A0A]'} flex flex-col items-center justify-center`}
  >
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-xs font-bold uppercase tracking-[0.15em]">{label}</span>
    </div>
    <div className="text-[10px] mt-1 opacity-70">
      {open} open · {count} total
    </div>
  </button>
);

const ItemCard = ({ item, tab, onTap }) => {
  const isDone = tab === 'checkouts' ? !!item.checked_out_at : !!item.completed_at;
  return (
    <button
      onClick={onTap}
      data-testid={`item-${item.source}-${item.id}`}
      className={`w-full text-left bg-white border p-4 active:scale-[0.99] transition-all ${isDone ? 'border-[#039855] opacity-70' : 'border-[#E5E5E5] hover:border-[#0A0A0A] hover:shadow-[4px_4px_0px_#0A0A0A]'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border border-[#E5E5E5] text-[#666] bg-white">{item.service_type}</span>
            {isDone && (
              <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 bg-[#039855] text-white flex items-center gap-1">
                <CheckCircle2 className="h-2.5 w-2.5" /> Done
              </span>
            )}
          </div>
          <p className="text-lg font-black truncate">{item.client}</p>
          <p className="text-sm text-[#666] truncate flex items-center gap-1.5 mt-0.5">
            <Car className="h-3.5 w-3.5 text-[#666] shrink-0" strokeWidth={2} /> {item.vehicle}
          </p>
          {item.location && (
            <p className="text-xs text-[#666] truncate flex items-center gap-1.5 mt-0.5">
              <MapPin className="h-3 w-3 shrink-0" strokeWidth={2} /> {item.location}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          {item.time && (
            <div className="text-2xl font-black font-mono tabular-nums">{item.time}</div>
          )}
          <Clock className="h-3 w-3 text-[#666] ml-auto mt-0.5" strokeWidth={2} />
        </div>
      </div>
    </button>
  );
};

const EmptyState = ({ tab }) => (
  <div className="text-center py-16 text-[#666]">
    <ClipboardCheck className="h-12 w-12 mx-auto mb-3 text-[#E5E5E5]" strokeWidth={1.5} />
    <p className="text-sm font-semibold">No {tab} for this date</p>
    <p className="text-xs mt-1">Pull-to-refresh or change the date above.</p>
  </div>
);

// ───────────────── Action Sheet (Complete Checkout / Return) ─────────────────
function ActionSheet({ item, tab, token, onClose, onDone }) {
  const isCheckout = tab === 'checkouts';
  const isDone = isCheckout ? !!item.checked_out_at : !!item.completed_at;
  const [mileage, setMileage] = useState('');
  const [fuel, setFuel] = useState('full');
  const [notes, setNotes] = useState('');
  const [damage, setDamage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [conditionFlow, setConditionFlow] = useState(null); // 'pre' | 'post' | null
  const [reports, setReports] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/condition-reports/by-booking/${item.id}`);
        const d = await r.json();
        if (!cancelled) setReports(d.reports || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [item.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const endpoint = isCheckout ? `checkout/${item.id}` : `return/${item.id}`;
      const r = await fetch(`${API}/api/staff-portal/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Staff-Token': token },
        body: JSON.stringify({
          source: item.source,
          mileage: mileage ? Number(mileage) : null,
          fuel,
          notes,
          damage,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      toast.success(isCheckout ? 'Checked out!' : 'Marked returned', { position: 'top-center' });
      onDone();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/staff-portal/note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Staff-Token': token },
        body: JSON.stringify({ booking_id: item.id, source: item.source, text: noteText.trim() }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Note added');
      setNoteText('');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={onClose}>
      <div
        className="bg-white border-t border-[#0A0A0A] w-full max-w-md max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="action-sheet"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-5 py-4">
          <div className="w-12 h-1.5 bg-[#E5E5E5] rounded-full mx-auto mb-3" />
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#E52B20] font-bold">{isCheckout ? 'Checkout' : 'Return'}</p>
              <h2 className="text-2xl font-black truncate mt-1">{item.client}</h2>
              <p className="text-sm text-[#666] truncate">{item.vehicle}</p>
            </div>
            <button onClick={onClose} className="text-[#666] hover:text-[#0A0A0A] text-2xl leading-none px-2" data-testid="sheet-close">×</button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Quick contact */}
          {item.phone && (
            <a href={`tel:${item.phone}`} className="flex items-center justify-between h-14 bg-white hover:border-[#0A0A0A] border border-[#E5E5E5] px-4 transition-colors">
              <span className="text-sm font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-[#039855]" /> Call client</span>
              <span className="text-sm text-[#666] font-mono">{item.phone}</span>
            </a>
          )}

          {/* Condition Report launcher */}
          <button
            onClick={() => setConditionFlow(isCheckout ? 'pre' : 'post')}
            className="w-full flex items-center justify-between h-14 bg-white hover:border-[#0A0A0A] border border-[#E5E5E5] px-4 transition-colors"
            data-testid="sheet-condition-report"
          >
            <span className="text-sm font-bold flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-[#E52B20]" />
              {isCheckout ? 'Start Pre-Rental Walkaround' : 'Start Post-Rental Walkaround'}
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#666] font-bold">{reports.length > 0 ? `${reports.length} on file` : 'New'}</span>
          </button>
          {reports.length > 0 && (
            <div className="space-y-1.5">
              {reports.map(r => (
                <div key={r.report_id} className="bg-[#F9F9FA] border border-[#E5E5E5] px-3 py-2 text-xs flex items-center justify-between" data-testid={`existing-report-${r.report_id}`}>
                  <div>
                    <span className="font-bold uppercase tracking-[0.15em] text-[10px] mr-1">{r.type}-rental</span>
                    <span className="text-[#666]">· {r.status}</span>
                    {r.mileage && <span className="text-[#666] ml-2">{r.mileage} mi</span>}
                  </div>
                  <span className="text-[#666] font-mono text-[10px]">{new Date(r.created_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}

          {isDone ? (
            <div className="bg-white border border-[#039855] p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-[#039855] mx-auto mb-2" />
              <p className="text-sm font-bold text-[#0A0A0A]">
                {isCheckout ? 'Vehicle checked out' : 'Vehicle returned'}
              </p>
              <p className="text-[10px] text-[#666] mt-1">
                {new Date(isCheckout ? item.checked_out_at : item.completed_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <>
              {/* Mileage */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold block mb-1.5">Mileage (optional)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={mileage}
                  onChange={e => setMileage(e.target.value)}
                  placeholder="e.g. 42891"
                  className="w-full h-12 px-3 bg-white border border-[#E5E5E5] text-base font-semibold focus:border-[#0A0A0A] outline-none transition-colors"
                  data-testid="sheet-mileage"
                />
              </div>

              {/* Fuel */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold block mb-1.5">Fuel Level</label>
                <div className="grid grid-cols-4 gap-2">
                  {['empty', '1/4', '1/2', '3/4', 'full'].slice(0, 4).map(f => (
                    <button
                      key={f}
                      onClick={() => setFuel(f)}
                      className={`h-12 text-xs font-bold uppercase ${fuel === f ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'} border transition-colors`}
                    >{f}</button>
                  ))}
                </div>
                <button
                  onClick={() => setFuel('full')}
                  className={`mt-2 w-full h-12 text-xs font-bold uppercase ${fuel === 'full' ? 'bg-[#039855] text-white border-[#039855]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'} border transition-colors`}
                >Full Tank</button>
              </div>

              {/* Damage (return only) */}
              {!isCheckout && (
                <button
                  onClick={() => setDamage(d => !d)}
                  className={`w-full h-12 flex items-center justify-center gap-2 font-bold uppercase text-xs border ${damage ? 'bg-[#F79009] text-white border-[#F79009]' : 'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]'} transition-colors`}
                  data-testid="sheet-damage"
                >
                  <AlertTriangle className="h-4 w-4" /> {damage ? 'Damage reported' : 'Report damage'}
                </button>
              )}

              {/* Notes */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold block mb-1.5">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Anything to flag..."
                  className="w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm focus:border-[#0A0A0A] outline-none transition-colors"
                  data-testid="sheet-notes"
                />
              </div>

              {/* Main CTA */}
              <button
                onClick={submit}
                disabled={busy}
                className="w-full h-16 bg-[#039855] hover:bg-[#02784a] active:scale-95 transition-all text-white font-black uppercase tracking-[0.15em] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="sheet-confirm"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-6 w-6" />}
                {isCheckout ? 'Confirm Checkout' : 'Confirm Return'}
              </button>
            </>
          )}

          {/* Quick note (always available) */}
          <div className="border-t border-[#E5E5E5] pt-4">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold flex items-center gap-2 mb-2">
              <MessageSquare className="h-3 w-3" /> Quick Note
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Log a quick observation..."
                className="flex-1 h-11 px-3 bg-white border border-[#E5E5E5] text-sm focus:border-[#0A0A0A] outline-none transition-colors"
                data-testid="sheet-note-text"
              />
              <button
                onClick={addNote}
                disabled={busy || !noteText.trim()}
                className="h-11 px-4 bg-white text-[#0A0A0A] border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs font-bold uppercase tracking-[0.15em] disabled:opacity-50 transition-colors"
                data-testid="sheet-note-add"
              >Add</button>
            </div>
          </div>
        </div>
      </div>

      {conditionFlow && (
        <ConditionReportFlow
          item={item}
          mode={conditionFlow}
          token={token}
          staffName=""
          onClose={() => setConditionFlow(null)}
          onComplete={() => { setConditionFlow(null); onDone(); }}
        />
      )}
    </div>
  );
}
